import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import { AlertTriangle, Info, X, Megaphone } from "lucide-react";

const DISMISSED_KEY = "fl_dismissed_broadcasts";

function getDismissed() {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"); } catch { return []; }
}
function setDismissed(ids) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
}

export function BroadcastBanner() {
  const [items, setItems] = useState([]);

  const fetchActive = useCallback(() => {
    api.get("/broadcasts/active").then((r) => {
      const dis = getDismissed();
      setItems(r.data.filter((b) => !dis.includes(b.id)));
    }).catch(() => {});
  }, []);
  usePoll(fetchActive, 60_000);

  useEffect(() => {
    // Immediate refresh whenever the platform admin creates/deletes a broadcast
    window.addEventListener("fl:broadcasts-changed", fetchActive);
    return () => window.removeEventListener("fl:broadcasts-changed", fetchActive);
  }, [fetchActive]);

  if (items.length === 0) return null;

  const dismiss = (id) => {
    setDismissed([...getDismissed(), id]);
    setItems((arr) => arr.filter((b) => b.id !== id));
  };

  return (
    <div className="space-y-1" data-testid="broadcast-banner">
      {items.map((b) => {
        const tone = b.severity === "critical"
          ? "bg-red-600 text-white border-red-700"
          : b.severity === "warning"
          ? "bg-yellow-400 text-zinc-900 border-yellow-500"
          : "bg-[#09090B] text-white border-zinc-900 dark:bg-[#151C27] dark:border-[#33363B]";
        const Icon = b.severity === "critical" ? AlertTriangle : b.severity === "warning" ? AlertTriangle : Megaphone;
        return (
          <div key={b.id} className={`flex items-center gap-3 px-4 py-2 border-b ${tone}`} data-testid={`broadcast-${b.id}`}>
            <Icon className="w-4 h-4 shrink-0" />
            <div className="flex-1 text-sm font-medium">{b.message}</div>
            <button onClick={() => dismiss(b.id)} className="text-current opacity-70 hover:opacity-100" data-testid={`broadcast-dismiss-${b.id}`} aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
